import { RuleSeverity } from '@atlas/types'
import type { RuleListItem, RuleListSeverity } from '@atlas/ui-domain'

/**
 * `RuleSeverity` onto the card's own union, exhaustively.
 *
 * `ApplicableRulesCard` keeps a hand-written union rather than importing the enum, so this is
 * the single place the two are tied together: a severity added to the enum fails to typecheck
 * here rather than reaching the card as a value it has no ordering for.
 */
const SEVERITY: Record<RuleSeverity, RuleListSeverity> = {
	[RuleSeverity.BLOCKING]: 'blocking',
	[RuleSeverity.WARNING]: 'warning',
}

/** The fields every applicable-rule shape in the app carries, whichever endpoint resolved it. */
export interface RuleForList {
	id: string
	statement: string
	severity: RuleSeverity
	type: string
	checkDefinition: string
}

/** `RuleSeverity` onto the card's own union — the mapping other adapters reuse directly. */
export const toRuleListSeverity = (severity: RuleSeverity): RuleListSeverity => SEVERITY[severity]

/**
 * An applicable rule as `ApplicableRulesCard` states it: statement, severity, type, and
 * what it checks for. This is the app's one resolved-rule row - a reader meets the same
 * rule the same way wherever they meet it.
 */
export const toRuleListItem = (rule: RuleForList): RuleListItem => ({
	id: rule.id,
	statement: rule.statement,
	severity: toRuleListSeverity(rule.severity),
	type: rule.type,
	checkDefinition: rule.checkDefinition,
})

export const toRuleListItems = (rules: RuleForList[]): RuleListItem[] => rules.map(toRuleListItem)
